// Québecle - Main Game Logic

import { Feedback, GameGuess, GameState, Region } from './types';
import { RegionService } from './regions';
import { StorageService } from './storage';
import { UIService } from './ui';
import { LONGEST_DISTANCE } from './consts';

export class GameService {
  private gameState: GameState = {
    currentRegion: null,
    guesses: [],
    attempts: 0,
    maxAttempts: 5,
    gameComplete: false,
    won: false,
    startTime: null,
    endTime: null,
    date: new Date().toISOString()
  };
  
  constructor(
    private regionManager: RegionService,
    private storageService: StorageService,
    private uiService: UIService
  ) {}
  
  // Initialize the game
  public async init(): Promise<void> {
    console.log('🎮 Initializing Québecle...');
    
    try {
      await this.regionManager.initializeRegions();
      this.storageService.init();
      await this.uiService.onInit(this);
      
      // Start or restore game
      this.startGame();
      
      console.log('✅ Game initialized successfully!');
    } catch (error) {
      console.error('❌ Failed to initialize game:', error);
      this.showInitializationError(error as Error);
    }
  }

  // Start a new game or restore existing one
  private startGame(): void {
    const savedGame = this.storageService.getCurrentGame();
    const today = new Date();
    
    if (savedGame && savedGame.date && this.isSameDay(new Date(savedGame.date), today)) {
      console.log('📥 Restoring saved game...');
      this.restoreGame(savedGame);
    } else {
      console.log('🆕 Starting new game...');
      this.initializeNewGame();
    }
  }

  // Initialize a new game
  private initializeNewGame(): void {
    const todayRegion = this.regionManager.getDailyRegion();
    
    this.gameState = {
      currentRegion: todayRegion,
      guesses: [],
      attempts: 0,
      maxAttempts: 5,
      gameComplete: false,
      won: false,
      startTime: new Date(),
      endTime: null,
      date: new Date().toISOString()
    };
    
    // Load region SVG
    if (todayRegion.svgFile) {
      this.uiService.loadRegionSVG(todayRegion.svgFile);
    }
    
    // Update UI
    this.uiService.updateAttemptsDisplay(this.gameState.maxAttempts);
    this.uiService.clearGuesses();
    this.uiService.enableInput();
    this.uiService.hideGameEndButtons();
    
    // Save initial game state
    this.storageService.saveCurrentGame(this.gameState);
    
    console.log(`🎯 Today's region: ${todayRegion.name}`);
  }

  // Restore a saved game
  private restoreGame(savedGame: GameState): void {
    this.gameState = { ...savedGame };
    
    // Load region SVG
    if (this.gameState.currentRegion?.svgFile) {
      this.uiService.loadRegionSVG(this.gameState.currentRegion.svgFile);
    }
    
    // Restore UI state
    const remainingAttempts = this.gameState.maxAttempts - this.gameState.attempts;
    this.uiService.updateAttemptsDisplay(remainingAttempts);
    this.uiService.clearGuesses();
    
    // Restore previous guesses
    this.gameState.guesses.forEach(guess => {
      this.uiService.addGuessToDisplay(guess, guess.correct);
    });
    
    // Check if game was completed
    if (this.gameState.gameComplete) {
      this.uiService.disableInput();
      // Optionally show results modal again
    } else {
      this.uiService.enableInput();
    }
    
    console.log(`📥 Restored game with ${this.gameState.attempts} attempts`);
  }
  
  // Make a guess
  public makeGuess(region: Region): void {
    if (this.gameState.gameComplete) {
      console.log('⚠️ Game already completed');
      return;
    }
    
    if (this.gameState.attempts >= this.gameState.maxAttempts) {
      console.log('⚠️ No attempts remaining');
      return;
    }
    
    // Check if region was already guessed
    const alreadyGuessed = this.gameState.guesses.some(guess => 
      (guess as any).region.id === region.id
    );
    
    if (alreadyGuessed) {
      // TODO: UIManager should have this method or we need different approach
      console.log('⚠️ Region already guessed');
      return;
    }
    
    console.log(`🎯 Guess: ${region.name}`);
    
    // Increment attempts
    this.gameState.attempts++;
    
    // Check if correct
    const isCorrect = Boolean(this.gameState.currentRegion && region.id === this.gameState.currentRegion.id);
    
    // Generate feedback
    const feedback = this.generateFeedback(region, isCorrect);
    
    // Create guess object
    const guess: GameGuess = {
      region: region,
      correct: isCorrect,
      feedback: feedback,
      timestamp: new Date()
    };
    
    // Add guess to game state
    this.gameState.guesses.push(guess);
    
    // Update UI
    this.uiService.addGuessToDisplay(guess, isCorrect);
    const remainingAttempts = this.gameState.maxAttempts - this.gameState.attempts;
    this.uiService.updateAttemptsDisplay(remainingAttempts);
    
    // Check if game is complete
    if (isCorrect || this.gameState.attempts >= this.gameState.maxAttempts) {
      this.endGame(isCorrect);
    } else {
      // Save current game state
      this.storageService.saveCurrentGame(this.gameState);
    }
  }
  
  // Generate feedback for a guess
  private generateFeedback(guessedRegion: Region, isCorrect: boolean): Feedback {
    const correctRegion = this.gameState.currentRegion;
    
    if (isCorrect && correctRegion) {
      return {
        guessedRegion: guessedRegion.name,
        distance: 0,
        direction: '⚜️',
        closeness: 1 // Perfect closeness
      };
    } else if (correctRegion) {
      // Provide proximity and comparison information
      const distance = this.regionManager.calculateDistance(guessedRegion, correctRegion);
      const direction = this.regionManager.calculateDirection(guessedRegion, correctRegion);
      const closeness = Math.max(0, LONGEST_DISTANCE - distance) / LONGEST_DISTANCE; // Normalized closeness
      
      return {
        guessedRegion: guessedRegion.name,
        distance: distance,
        direction: direction,
        closeness: closeness
      };
    }
    
    return {} as Feedback;
  }

  // End the game
  private endGame(won: boolean): void {
    console.log(`🎮 Game ended - ${won ? 'Won' : 'Lost'}`);
    
    // Update game state
    this.gameState.gameComplete = true;
    this.gameState.won = won;
    this.gameState.endTime = new Date();
    
    // Update statistics
    this.storageService.updateStats(won, this.gameState.attempts);
    
    // Clear current game from storage
    this.storageService.clearCurrentGame();
    
    // Update UI
    this.uiService.disableInput();
    if (this.gameState.currentRegion) {
      this.uiService.onGameEnd(won, this.gameState.currentRegion, this.gameState.attempts);
    }
    
    console.log(`📊 Final score: ${this.gameState.attempts}/${this.gameState.maxAttempts}`);
  }

  // Get current game state
  public getGameState(): GameState {
    return { ...this.gameState };
  }  /** */
  // Check if two dates are the same day
  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
  }
  
  // Reset game (for testing purposes)
  public resetGame(): void {
    console.log('🔄 Resetting game...');
    
    // Clear storage
    this.storageService.clearCurrentGame();
    
    // Start new game
    this.initializeNewGame();
  }
  
  // Get game statistics
  public getStats(): any {
    return this.storageService.getStats();
  }
  
  // Export game data
  public exportData(): any {
    return {
      gameState: this.gameState,
      storageData: this.storageService.exportData(),
      exportDate: new Date().toISOString()
    };
  }

  // Get time until next game
  public getTimeUntilNextGame(): { hours: number; minutes: number; seconds: number; ms: number } {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const msUntilTomorrow = tomorrow.getTime() - now.getTime();
    
    const hours = Math.floor(msUntilTomorrow / (1000 * 60 * 60));
    const minutes = Math.floor((msUntilTomorrow % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((msUntilTomorrow % (1000 * 60)) / 1000);
    
    return { hours, minutes, seconds, ms: msUntilTomorrow };
  }

  // Check if it's a new day and reset if needed
  public checkNewDay(): void {
    const savedGame = this.storageService.getCurrentGame();
    if (!savedGame || !savedGame.date) return;
    
    const savedDate = new Date(savedGame.date);
    const today = new Date();
    
    if (!this.isSameDay(savedDate, today)) {
      console.log('📅 New day detected, starting fresh game');
      this.initializeNewGame();
    }
  }

  // Show initialization error to user
  private showInitializationError(error: Error): void {
    const container = document.querySelector('.container');
    if (container) {
      container.innerHTML = `
                <div class="error-container" style="text-align: center; padding: 40px;">
                    <h2 style="color: var(--danger-color);">❌ Erreur d'initialisation</h2>
                    <p>Impossible de charger les données des régions du Québec.</p>
                    <p style="font-size: 0.9rem; color: #666; margin-top: 20px;">
                        Erreur: ${error.message}
                    </p>
                    <button onclick="location.reload()" 
                            style="margin-top: 20px; padding: 10px 20px; background: var(--primary-color); color: white; border: none; border-radius: 5px; cursor: pointer;">
                        Réessayer
                    </button>
                </div>
            `;
    }
  }
}

